/**
 * A scratchpad page renderer, as converted from tutorial-scratchpad.handlebars.
 */
import "../../stylesheets/shared-package/shared.less";
import "../../stylesheets/moderation-package/moderation.less";
import "../../stylesheets/discussion-package/discussion.less";
import "../../stylesheets/avatar-customizer-package/avatar-customizer.less";
import "../../stylesheets/odometer-package/odometer.less";
import "../../stylesheets/tasks-package/tasks.less";
import "../../stylesheets/intro-package/intro.less";
// TODO(joel): Extract the discussions related stuff out of the video package
import "../../stylesheets/video-package/video.less";
// TODO(pamelafox): Extract into a separate package
import "../../stylesheets/clarifications-package/clarifications.less";
// These scratchpad imports must come last, or else some of the CSS rules will
// be overwritten by other imports, which will break things, e.g. the image
// picker modal.
import "../../stylesheets/scratchpad-challenge-package/scratchpad-challenge.less";
import "../../stylesheets/scratchpads-package/scratchpads.less";

import {compose} from "mu-lambda";
import * as React from "react";

import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {
    withActionScheduler,
    type WithActionSchedulerProps,
    type WithoutActionScheduler,
} from "@khanacademy/wonder-blocks-timing";
import {GoogleAnalytics, sendAnEventToGA4} from "@khan/analytics-google";
import {setPromptVariable} from "@khan/guide-core-helpers";
import {Errors, KAError} from "@khan/logging";

import {PROGRAM_STATUS} from "#scratchpad-shared-package/types.ts";
import {ScratchpadUIStart} from "#scratchpads-package/scratchpad-ui-start.ts";
import {ScratchpadUI} from "#scratchpads-package/scratchpad-ui.tsx";
import {getTermMap} from "#scratchpads-package/user-authored-content-types.ts";
import APIActionResults from "#shared-package/api-action-results.ts";
import type {ProfileModelType} from "#shared-package/profile-model.ts";
import type {
    ScratchpadContentModel,
    TutorialPageTopic,
} from "#tutorial-types-package/types.ts";

import type {NewScratchpadData, NewScratchpadTopic} from "./types.ts";
import type {CSContentOptions, ProgramOptions} from "./use-program.ts";

type OwnProps = {
    options: Readonly<
        Partial<CSContentOptions | ProgramOptions> & {
            scratchpad:
                | ProgramOptions["scratchpad"] // used by new-scratchpad-page.jsx
                | NewScratchpadData
                | ScratchpadContentModel; // used by project-renderer.jsx
            topic:
                | ProgramOptions["topic"] // used by new-scratchpad-page.jsx
                | NewScratchpadTopic
                | TutorialPageTopic; // used by project-renderer.jsx
        }
    >;
    profile: ProfileModelType | null | undefined;
    style?: StyleType;
};

type Props = OwnProps & WithActionSchedulerProps;

class ScratchpadPage extends React.Component<Props> {
    componentDidMount() {
        this._isMounted = true;

        const {scratchpad} = this.props.options;
        // @ts-expect-error - TS2339 - Property 'contentKind' does not exist on type 'NewScratchpadData | ScratchpadTalkthrough | ScratchpadInteractive | ScratchpadChallenge | ScratchpadProject | USER_PROGRAM_SCRATCHPAD_SIG | (USER_PROGRAM_SCRATCHPAD_SIG & NewScratchpadData)'.
        const {contentKind} = scratchpad;

        this.logConversions();
        this.onInitScratchPad();

        if (contentKind === "Project") {
            this.updateProjectCompletionState();
        }

        if (contentKind === "Interactive") {
            this.updateInteractiveCompletionState();
        }
    }

    componentWillUnmount() {
        // Remove Scratchpad UI when this component is unmounted.
        // To avoids talk-throughs from continuing to play when
        // navigating away from a page.
        ScratchpadUI.triggerRemove();
        this._isMounted = false;
    }

    // TODO(briangenisio): This is a hack to remove the deprecated call to
    // this.isMounted() but is still considered an anti-pattern.  Replace
    // with code which does not rely on the mounted state like Redux or GraphQL
    _isMounted = false;

    logConversions: () => void = () => {
        const {scratchpad} = this.props.options;
        // @ts-expect-error - TS2339 - Property 'nodeSlug' does not exist on type 'NewScratchpadData | ScratchpadTalkthrough | ScratchpadInteractive | ScratchpadChallenge | ScratchpadProject | USER_PROGRAM_SCRATCHPAD_SIG | (USER_PROGRAM_SCRATCHPAD_SIG & NewScratchpadData)'.
        const {nodeSlug} = scratchpad;

        GoogleAnalytics.sendToGA(
            "event",
            "Learning-Action",
            "Scratchpad-View",
            nodeSlug,
        );

        sendAnEventToGA4({
            event: "learningAction",
            actionType: "Scratchpad-View",
            slug: nodeSlug,
        });
    };

    updateInteractiveCompletionState: () => void = () => {
        const {options, schedule} = this.props;
        // @ts-expect-error - TS2339 - Property 'updateInteractiveProgress' does not exist on type 'Readonly<Partial<CSContentOptions | ProgramOptions> & { scratchpad: NewScratchpadData | ScratchpadContentModel | USER_PROGRAM_SCRATCHPAD_SIG; topic: NewScratchpadTopic | ... 1 more ... | USER_PROGRAM_TOPIC_SIG; }>'. | TS2339 - Property 'onComplete' does not exist on type 'Readonly<Partial<CSContentOptions | ProgramOptions> & { scratchpad: NewScratchpadData | ScratchpadContentModel | USER_PROGRAM_SCRATCHPAD_SIG; topic: NewScratchpadTopic | ... 1 more ... | USER_PROGRAM_TOPIC_SIG; }>'.
        const {scratchpad, topic, updateInteractiveProgress, onComplete} =
            options;
        if (
            // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'NewScratchpadData | ScratchpadTalkthrough | ScratchpadInteractive | ScratchpadChallenge | ScratchpadProject | USER_PROGRAM_SCRATCHPAD_SIG | (USER_PROGRAM_SCRATCHPAD_SIG & NewScratchpadData)'.
            !scratchpad.id ||
            // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'NewScratchpadTopic | TutorialPageTopic | ((USER_PROGRAM_TOPIC_SIG | undefined) & (NewScratchpadTopic | TutorialPageTopic | USER_PROGRAM_TOPIC_SIG))'.
            !topic.id ||
            !updateInteractiveProgress ||
            // @ts-expect-error - TS2339 - Property 'contentKind' does not exist on type 'NewScratchpadData | ScratchpadTalkthrough | ScratchpadInteractive | ScratchpadChallenge | ScratchpadProject | USER_PROGRAM_SCRATCHPAD_SIG | (USER_PROGRAM_SCRATCHPAD_SIG & NewScratchpadData)'.
            scratchpad.contentKind !== "Interactive"
        ) {
            return;
        }

        try {
            schedule.timeout(async () => {
                const result = await updateInteractiveProgress({
                    // ScratchpadPage handles both user created programs and
                    // content programs. The types representing both are
                    // actively being updated at this time. Once we properly
                    // type them, then we can probably remove the Flow
                    // suppression.
                    // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'NewScratchpadData | ScratchpadTalkthrough | ScratchpadInteractive | ScratchpadChallenge | ScratchpadProject | USER_PROGRAM_SCRATCHPAD_SIG | (USER_PROGRAM_SCRATCHPAD_SIG & NewScratchpadData)'.
                    programId: scratchpad.id,
                    // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'NewScratchpadTopic | TutorialPageTopic | ((USER_PROGRAM_TOPIC_SIG | undefined) & (NewScratchpadTopic | TutorialPageTopic | USER_PROGRAM_TOPIC_SIG))'.
                    lessonId: topic.id,
                });

                if (result.error) {
                    throw result.error;
                }

                const data = result.data?.updateInteractiveProgress;
                if (data?.error) {
                    throw new KAError(data.error.code, Errors.Internal);
                }

                if (data?.programProgress?.completionStatus === "COMPLETE") {
                    onComplete?.();
                }
                if (data?.actionResults) {
                    // APIActionResult is using types without __typename – this
                    // was introduced before we generated types with gqlflow.
                    // We'll need to update our types or APIActionResults to
                    // support these generated GQL types.
                    APIActionResults.respondToGraphQLAction(data.actionResults);
                }
            }, 1000);
        } catch (e: any) {
            // We handle the exception thrown from within the mutation success
            // handler.
            if (e instanceof KAError) {
                throw e;
            }

            // Otherwise we throw a generic error.
            throw new KAError(
                "Failed to update interactive content progress",
                Errors.Internal,
            );
        }
    };

    updateProjectCompletionState: () => void = () => {
        const {options, schedule} = this.props;
        // @ts-expect-error - TS2339 - Property 'updateProjectProgress' does not exist on type 'Readonly<Partial<CSContentOptions | ProgramOptions> & { scratchpad: NewScratchpadData | ScratchpadContentModel | USER_PROGRAM_SCRATCHPAD_SIG; topic: NewScratchpadTopic | ... 1 more ... | USER_PROGRAM_TOPIC_SIG; }>'. | TS2339 - Property 'onComplete' does not exist on type 'Readonly<Partial<CSContentOptions | ProgramOptions> & { scratchpad: NewScratchpadData | ScratchpadContentModel | USER_PROGRAM_SCRATCHPAD_SIG; topic: NewScratchpadTopic | ... 1 more ... | USER_PROGRAM_TOPIC_SIG; }>'. | TS2339 - Property 'onStart' does not exist on type 'Readonly<Partial<CSContentOptions | ProgramOptions> & { scratchpad: NewScratchpadData | ScratchpadContentModel | USER_PROGRAM_SCRATCHPAD_SIG; topic: NewScratchpadTopic | ... 1 more ... | USER_PROGRAM_TOPIC_SIG; }>'.
        const {scratchpad, topic, updateProjectProgress, onComplete, onStart} =
            options;
        if (
            // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'NewScratchpadData | ScratchpadTalkthrough | ScratchpadInteractive | ScratchpadChallenge | ScratchpadProject | USER_PROGRAM_SCRATCHPAD_SIG | (USER_PROGRAM_SCRATCHPAD_SIG & NewScratchpadData)'.
            !scratchpad.id ||
            // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'NewScratchpadTopic | TutorialPageTopic | ((USER_PROGRAM_TOPIC_SIG | undefined) & (NewScratchpadTopic | TutorialPageTopic | USER_PROGRAM_TOPIC_SIG))'.
            !topic.id ||
            !updateProjectProgress ||
            // @ts-expect-error - TS2339 - Property 'contentKind' does not exist on type 'NewScratchpadData | ScratchpadTalkthrough | ScratchpadInteractive | ScratchpadChallenge | ScratchpadProject | USER_PROGRAM_SCRATCHPAD_SIG | (USER_PROGRAM_SCRATCHPAD_SIG & NewScratchpadData)'.
            scratchpad.contentKind !== "Project"
        ) {
            return;
        }

        try {
            schedule.timeout(async () => {
                const result = await updateProjectProgress({
                    // ScratchpadPage handles both user created programs and
                    // content programs. The types representing both are
                    // actively being updated at this time. Once we properly
                    // type them, then we can probably remove the Flow
                    // suppression.
                    // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'NewScratchpadData | ScratchpadTalkthrough | ScratchpadInteractive | ScratchpadChallenge | ScratchpadProject | USER_PROGRAM_SCRATCHPAD_SIG | (USER_PROGRAM_SCRATCHPAD_SIG & NewScratchpadData)'.
                    programId: scratchpad.id,
                    // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'NewScratchpadTopic | TutorialPageTopic | ((USER_PROGRAM_TOPIC_SIG | undefined) & (NewScratchpadTopic | TutorialPageTopic | USER_PROGRAM_TOPIC_SIG))'.
                    lessonId: topic.id,
                    status: PROGRAM_STATUS.PROJECT_STARTED,
                });

                if (result.error) {
                    throw result.error;
                }

                const data = result.data?.updateProjectProgress;
                if (data?.error) {
                    throw new KAError(data.error.code, Errors.Internal);
                }

                if (data?.programProgress?.completionStatus === "STARTED") {
                    onStart?.();
                }
                if (data?.programProgress?.completionStatus === "COMPLETE") {
                    onComplete?.();
                }
                if (data?.actionResults) {
                    // APIActionResult is using types without __typename – this
                    // was introduced before we generated types with gqlflow.
                    // We'll need to update our types or APIActionResults to
                    // support these generated GQL types.
                    APIActionResults.respondToGraphQLAction(data.actionResults);
                }
            }, 1000);
        } catch (e: any) {
            // We handle the exception thrown from within the mutation success
            // handler.
            if (e instanceof KAError) {
                throw e;
            }

            // Otherwise we throw a generic error.
            throw new KAError(
                "Failed to update project content progress",
                Errors.Internal,
            );
        }
    };

    // Truncate the code of extremely large programs so that it will
    // fit in the LLM's context window.
    trimCodeForKhanmigo(code: string): string {
        // ~8k tokens for gpt-4 * ~3 characters = 24k characters
        // Max code size is set to half of that to account
        // for the rest of the prompt and the conversation.
        const maxCodeSize = 12000;

        return code.substring(0, maxCodeSize);
    }

    async onInitScratchPad() {
        const {
            profile,
            options: {scratchpad},
        } = this.props;

        if (profile?.get("isAIGuideEnabled")) {
            // @ts-expect-error - TS2339 - Property 'revision' does not exist on type 'NewScratchpadData | ScratchpadContentModel | USER_PROGRAM_SCRATCHPAD_SIG | (USER_PROGRAM_SCRATCHPAD_SIG & NewScratchpadData)'.
            const code = scratchpad?.revision?.code ?? "";
            const language = scratchpad?.userAuthoredContentType ?? "";

            setPromptVariable("code", this.trimCodeForKhanmigo(code));
            setPromptVariable("programming_language", language);
        }

        const onCodeChange = (data: any) => {
            // We don't want to generate side effects if the user doesn't have
            // the AI Guide enabled.
            if (!profile?.get("isAIGuideEnabled")) {
                return;
            }

            if (data?.results?.code) {
                setPromptVariable(
                    "code",
                    this.trimCodeForKhanmigo(data?.results?.code),
                );
            }
        };

        if (!this._isMounted) {
            return;
        }

        // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'NewScratchpadData | ScratchpadTalkthrough | ScratchpadInteractive | ScratchpadChallenge | ScratchpadProject | USER_PROGRAM_SCRATCHPAD_SIG | (USER_PROGRAM_SCRATCHPAD_SIG & NewScratchpadData)'. | TS2339 - Property 'contentKind' does not exist on type 'NewScratchpadData | ScratchpadTalkthrough | ScratchpadInteractive | ScratchpadChallenge | ScratchpadProject | USER_PROGRAM_SCRATCHPAD_SIG | (USER_PROGRAM_SCRATCHPAD_SIG & NewScratchpadData)'.
        const {id, userAuthoredContentType, contentKind} = scratchpad;

        ScratchpadUI.termMap = getTermMap(userAuthoredContentType);
        await ScratchpadUIStart(this.props.options);

        ScratchpadUI.init({
            ...this.props.options,
            id,
            userAuthoredContentType,
            profile,
            contentKind,
            onCodeChange,
        });
    }

    render(): React.ReactNode {
        const {options, style} = this.props;
        const {scratchpad} = options;

        return (
            // @ts-expect-error - TS2339 - Property 'id' does not exist on type 'NewScratchpadData | ScratchpadTalkthrough | ScratchpadInteractive | ScratchpadChallenge | ScratchpadProject | USER_PROGRAM_SCRATCHPAD_SIG | (USER_PROGRAM_SCRATCHPAD_SIG & NewScratchpadData)'.
            <View key={scratchpad.id} style={style}>
                <div className="box-sizing-content-box-reset">
                    <div className="scratchpad-page bibliotron-scratchpad" />
                </div>
            </View>
        );
    }
}

type ExportProps = WithoutActionScheduler<
    JSX.LibraryManagedAttributes<
        typeof ScratchpadPage,
        React.ComponentProps<typeof ScratchpadPage>
    >
>;

export default compose(withActionScheduler)(
    ScratchpadPage,
) as React.ComponentType<ExportProps>;
